import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input, Divider, Skeleton, Tooltip } from 'antd';
import {
  HC200Icon,
  SearchIcon,
  FilterIcon,
  NewFilterIcon,
  AccountAddIcon,
  RefreshIcon,
  FactoryResetIcon,
  SyncIcon,
} from '../Common/Icons';
import { Actions, DeviceStatus } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import MainPage from '../../MainPage';
import CustomDropdownCheckBox from '../Common/CustomDropdownCheckBox';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  KnownActionsList,
  isStaffActionAllowed,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import { SESSION_STORAGE_KEYS } from '@/constants/device';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { useSelector } from 'react-redux';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import RebootResetModal from './RebootResetModal';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import Pagination from '../Pagination/Pagination';
import { hasOnlyStaffAdminPrivileges } from '../DeviceInventory/helpers';
import NewCustomButton from '@/Components/Common/NewCustomButton';
import { NewRefreshIcon } from '@/Components/Common/Icons/NewRefreshIcon';
import { DEPLOYMENT_STATES } from '@/constants/device';

const DevicesComponent = ({
  deviceData,
  fetchingDeviceData,
  providerId,
  onActionClick,
  deviceCount,
  showPagination,
  offset,
  limit,
  userHubsData,
  setQueryParams = () => {},
}) => {
  const getDeploymentState = (device) => device?.deployment_state ?? null;
  const matchesAccountFilter = (device, accountFilters) => {
    if (accountFilters.length === 0) return true;

    const deploymentState = getDeploymentState(device);
    return accountFilters.some((filter) => {
      if (filter === 'demoAccount') return deploymentState === DEPLOYMENT_STATES.NOT_DEPLOYED;
      if (filter === 'deployedWithClient') {
        return deploymentState === 'DEPLOYED' || deploymentState === null;
      }
      return true;
    });
  };

  const [deviceList, setDeviceList] = useState(deviceData);
  const [searchText, setSearchText] = useState('');
  const [isFilterOn, setIsFilterOn] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState([]);
  const [modalData, setModalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const deviceManagementApi = useSelector((state) => state.apis.deviceManagementApi);
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);

  const FilterItems = [
    {
      key: 'connected',
      type: 'group',
      label: trans('CONNECTED'),
      children: [
        {
          key: 'online',
          label: trans('ONLINE'),
        },
        {
          key: 'offline',
          label: trans('OFFLINE'),
        },
      ],
    },
    ...(currentUser?.permission_level === 'Staff'
      ? []
      : [
          {
            key: 'accounts',
            type: 'group',
            label: trans('ACCOUNTS'),
            children: [
              {
                key: 'demoAccount',
                label: trans('DEMO'),
              },
              {
                key: 'deployedWithClient',
                label: trans('CLIENT'),
              },
              {
                key: 'assigned',
                label: trans('ASSIGNED'),
              },
              {
                key: 'readyToAssign',
                label: trans('READYTOASSIGN'),
              },
              {
                key: 'notActivated',
                label: trans('NOTACTIVATED'),
              },
              {
                key: 'notReady',
                label: trans('NOTREADY'),
              },
            ],
          },
        ]),
  ];
  const permissions = useSelector(getPermissions());
  const actionsData = {
    reboot: {
      title: trans('REFRESH'),
      description: trans('DEVICESREBOOTDESCRIPTION'),
      action: 'device_reboot',
      icon: <RefreshIcon className="icon custom-icon-color" />,
    },
    reset: {
      title: trans('FACTORYRESET'),
      description: trans('DEVICESRESETDESCRIPTION'),
      action: 'factory_reset',
      icon: <FactoryResetIcon className="icon" style={{ fill: '#005c98' }} />,
    },
  };

  useEffect(() => {
    handleSearchApply();
  }, []);

  useEffect(() => {
    setDeviceList(deviceData);
    const savedFilter = SessionStorage.get(SESSION_STORAGE_KEYS.INVENTORY_FILTER);
    if (savedFilter) {
      setAppliedFilter(savedFilter);
    }
  }, [deviceData]);

  useEffect(() => {
    if (appliedFilter.length === 0) {
      handleSearchApply();
    }
  }, [appliedFilter]);

  const handleSearchApply = (filterKeys = appliedFilter, search = searchText) => {
    if (!Array.isArray(filterKeys)) {
      filterKeys = [];
    }
    let params = {
      filterkey: 'category',
      filtervalue: 'DEVICE-PRIMARY',
      sortby: 'deviceid',
      orderby: 'asc',
    };

    if (search) {
      const query = search.toLowerCase();
      let isDeviceId = false;

      if (deviceData.some((device) => device?.deviceid?.toLowerCase().includes(query))) {
        isDeviceId = true;
      }

      if (isDeviceId) {
        params.filterkey += ',deviceid';
        params.filtervalue += `,|${query}|`;
      } else {
        params.filterkey += ',accountname';
        params.filtervalue += `,|${query}|`;
      }
    }

    const connectedFilters = filterKeys
      .filter((f) => f.groupKey === 'connected')
      .map((f) => f.itemKey);
    const accountFilters = filterKeys
      .filter((f) => f.groupKey === 'accounts')
      .map((f) => f.itemKey);

    if (connectedFilters.length > 0) {
      const connectionFilter = connectedFilters.includes('online') ? 'CONNECTED' : 'DISCONNECTED';
      params.filterkey += ',deviceconnectstatus';
      params.filtervalue += `,${connectionFilter}`;
    }

    accountFilters.forEach((itemKey) => {
      if (itemKey === 'assigned') {
        // When the user is assigned to the device
        params.filterkey += ',useruuid';
        params.filtervalue += ',not empty';
      } else if (itemKey === 'readyToAssign') {
        // When the device is in provisioned state and user is not assigned to the device and device is clear from old user data
        params.filterkey += ',-useruuid,provisioningstatus,-operationalstatus';
        params.filtervalue += ',not empty,Provisioned,TO_DO_FACTORYRESET';
      } else if (itemKey === 'notActivated') {
        // When the device is not in provisioned state and user is not assigned to the device
        params.filterkey += ',-provisioningstatus,-useruuid';
        params.filtervalue += ',Provisioned,not empty';
      } else if (itemKey === 'notReady') {
        // When the device is in provisioned state and user is not assigned to the device to be removed from factory reset
        params.filterkey += ',-useruuid,provisioningstatus,operationalstatus';
        params.filtervalue += ',not empty,Provisioned,TO_DO_FACTORYRESET';
      }
    });

    setQueryParams(params);
  };

  const handleSearchClear = () => {
    setSearchText('');
    setDeviceList(deviceData);
    handleSearchApply(appliedFilter, '');
  };

  const handleFilterToggle = (e) => {
    // Stop the event propagation to prevent it from reaching the parent div
    e.stopPropagation();
    setIsFilterOn((value) => !value);
  };

  const handleFilterChange = (groupKey, itemKey) => {
    // Check if the clicked filter is already applied
    const isFilterApplied = appliedFilter.some(
      (filter) => filter.groupKey === groupKey && filter.itemKey === itemKey
    );

    let newFilters = [];

    if (isFilterApplied) {
      // If the filter is already applied, remove it
      newFilters = appliedFilter.filter(
        (filter) => !(filter.groupKey === groupKey && filter.itemKey === itemKey)
      );
    } else {
      newFilters = appliedFilter.filter((filter) => filter.groupKey !== groupKey);
      newFilters.push({ groupKey, itemKey });
    }
    setAppliedFilter(newFilters);
    handleSearchApply(newFilters, searchText);
    SessionStorage.set(SESSION_STORAGE_KEYS.INVENTORY_FILTER, newFilters);
  };

  const handleClearAllFilters = () => {
    setAppliedFilter([]);
    handleSearchClear();
    onActionClick(null, null, offset, limit)();
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.INVENTORY_FILTER);
  };

  const cancelCallback = () => {
    setShowModal(false);
    setModalData({});
  };
  const refreshCallback = (data) => {
    performAction(data);
    setShowModal(false);
  };
  const resetCallback = (data) => {
    performAction(data);
    setShowModal(false);
  };

  const handleRefresh = (device) => {
    setShowModal(true);
    let data = {
      ...actionsData.reboot,
      deviceUuid: device.deviceuuid,
      proceed: refreshCallback,
      cancel: cancelCallback,
    };
    setModalData(data);
  };
  const handleReset = (device) => {
    setShowModal(true);
    let data = {
      ...actionsData.reset,
      deviceUuid: device.deviceuuid,
      proceed: resetCallback,
      cancel: cancelCallback,
    };
    setModalData(data);
  };

  const performAction = (data) => {
    const errorCallback = (error) => {
      // message.error("Error performing: " + data.title + " " + error.message);
      console.error('Error performing:' + data.title + ' ' + error.message);
    };
    const successCallback = (info) => {
      // message.success("Signal sent to Device for: " + data.title);
    };

    try {
      deviceManagementApi.performCommand(
        data.action,
        providerId,
        data.deviceUuid,
        { source: 'System' },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const getDeviceStatus = (device) => {
    const isNotReadyFilterApplied = appliedFilter.some((filter) => filter.itemKey === 'notReady');
    const deviceStatus = device.devicestatus?.toLowerCase();
    const provisioningStatus = device.provisioningstatus?.toLowerCase();
    const operationalStatus = device.operationalstatus?.toLowerCase();

    if (device.useruuid) {
      if (
        isNotReadyFilterApplied &&
        provisioningStatus === 'provisioned' &&
        operationalStatus === 'to_do_factoryreset'
      ) {
        return DeviceStatus.not_ready;
      }
      return DeviceStatus.userassociated;
    }

    if (
      deviceStatus === 'active' &&
      provisioningStatus === 'provisioned' &&
      operationalStatus !== 'to_do_factoryreset'
    ) {
      return DeviceStatus.provisioned;
    }

    if (provisioningStatus === 'provisioned' && operationalStatus === 'to_do_factoryreset') {
      return DeviceStatus.not_ready;
    }
    return DeviceStatus.not_activated;
  };

  useEffect(() => {
    const savedFilter = SessionStorage.get(SESSION_STORAGE_KEYS.INVENTORY_FILTER);
    if (savedFilter) {
      setAppliedFilter(savedFilter);
      handleSearchApply(savedFilter, searchText);
    }
  }, []);

  const allowAssignDevice = (device, status) => {
    return (
      [DeviceStatus.provisioned, DeviceStatus.active_not_provisioned].includes(status) &&
      isStaffActionAllowed(
        currentUser?.permission_level,
        KnownActionsList.manage_devices,
        permissions
      ) &&
      device.devicestatus === 'ACTIVE'
    );
  };

  const renderDeviceStatus = (device, status) => {
    return status === DeviceStatus.userassociated ? (
      <div className="account-name">{device.accountname}</div>
    ) : status === DeviceStatus.provisioned ? (
      <div className="device-status-ready-to-assign">
        <div className="device-status-text">{trans('READYTOASSIGN')}</div>
      </div>
    ) : status === DeviceStatus.not_ready ? (
      <div className="device-status-not-ready">
        <div className="device-status-text">{trans('NOTREADY')}</div>
      </div>
    ) : (
      <div className="device-status-not-activated">
        <div className="device-status-text">{trans('NOTACTIVATED')}</div>
      </div>
    );
  };

  const displayDeviceList = useMemo(() => {
    const accountFilters = appliedFilter
      .filter((filter) => filter.groupKey === 'accounts')
      .map((filter) => filter.itemKey);

    // Check if the user is Staff
    if (currentUser?.permission_level === 'Staff') {
      // Get a list of device UUIDs assigned to the current user from userHubsData
      const assignedDeviceUUIDs = userHubsData.map((hub) => hub.deviceuuid);
      // Filter deviceList to only show devices assigned to the current user
      const filteredDevices = deviceList.filter((device) =>
        assignedDeviceUUIDs.includes(device.deviceuuid)
      );
      return filteredDevices.filter((device) => matchesAccountFilter(device, accountFilters));
    } else {
      // If not Staff, show the existing device list
      return deviceList.filter((device) => matchesAccountFilter(device, accountFilters));
    }
  }, [appliedFilter, currentUser, deviceList, userHubsData]);

  return (
    <>
      {showModal && <RebootResetModal data={modalData} />}
      <MainPage hasSider={true}>
        <div className="devices-container page-content-wrapper">
          <div className="page-header-container page-header-container-css">
            <span className="page-header">
              <span>{trans('DEVICES')}</span>
              <NewCustomButton
                className="cancel"
                onClick={() => handleClearAllFilters()}
                label={trans('REFRESH')}
                icon={<NewRefreshIcon className="cancel-icon" />}
              />
            </span>
            <div className="header-action-container">
              <div className="search-container">
                <Input
                  autoFocus
                  className="search-acc-input"
                  value={searchText}
                  placeholder={trans('SEARCH')}
                  prefix={<SearchIcon />}
                  onChange={(e) => setSearchText(e?.target?.value)}
                  onPressEnter={() => handleSearchApply(appliedFilter, searchText)}
                  suffix={
                    searchText ? (
                      <CloseOutlined onClick={handleSearchClear} title={trans('CLEARSEARCH')} />
                    ) : (
                      <span />
                    )
                  }
                />
              </div>
              <div className="filter-container inventory-filter-container">
                <CustomDropdownCheckBox
                  open={isFilterOn}
                  leftMenuItems={FilterItems}
                  showActionButtons={false}
                  selectedKeys={appliedFilter}
                  handleChange={handleFilterChange}
                  onClick={handleFilterToggle}
                  className="accounts-filter-dropdown inventory-filter"
                  hideGroupTitles
                >
                  <NewFilterIcon className="filter-icon" />
                  <span className="text filter-text">{trans('FILTERS')}</span>
                </CustomDropdownCheckBox>
              </div>
            </div>
          </div>
          <div className="item-count-container-css  item-count-container ">
            <span className="item-count">
              {fetchingDeviceData ? <LoadingOutlined /> : (displayDeviceList.length ?? 0)}{' '}
              {trans('TOTAL')}
            </span>
          </div>
          <div className="devices-list-container item-list-container">
            {fetchingDeviceData ? (
              <div className="device-container skeleton-container ">
                <Skeleton active avatar paragraph={false} />
              </div>
            ) : (
              displayDeviceList.map((device) => {
                let isUserAssociated = userHubsData.some((hub) => hub.useruuid === device.useruuid);
                let status = getDeviceStatus(device);
                return (
                  <div
                    key={device.deviceuuid}
                    className="device-container item-container container-card-css"
                  >
                    <div
                      className={`container-1 ${
                        status === DeviceStatus.userassociated ? 'device-accountname' : ''
                      }`}
                      onClick={
                        status === DeviceStatus.userassociated && isUserAssociated
                          ? onActionClick(Actions.listHub, device, offset, limit)
                          : null
                      }
                    >
                      <div
                        title={
                          device.deviceconnectstatus
                            ? trans(device.deviceconnectstatus.toUpperCase())
                            : ''
                        }
                        className="device-icon-container"
                      >
                        <HC200Icon status={device.deviceconnectstatus?.toLowerCase()} />
                      </div>
                      <div className="device-sn">{device.deviceid}</div>
                      {renderDeviceStatus(device, status)}
                      {getDeploymentState(device) === DEPLOYMENT_STATES.NOT_DEPLOYED && (
                        <div className="device-demo-tag-container">
                          <span className="device-demo-tag-text">{trans('DEMO')}</span>
                        </div>
                      )}
                    </div>
                    <div className="container-2">
                      <div className="actions-container">
                        <Divider type="vertical" />
                        {/*DEPRECATED: Add account in devices from Inventory */}
                        {/* {allowAssignDevice(device, status) ? (
                          <div
                            className="action"
                            onClick={onActionClick(
                              Actions.assignDevice,
                              device,
                              offset,
                              limit
                            )}
                          >
                            <Tooltip title={trans("ADDACCOUNT")}>
                              <div>
                                <AccountAddIcon className="account-add-icon" />
                              </div>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="empty-action" />
                        )} */}
                        {![DeviceStatus.not_activated].includes(status) ? (
                          <div className="action">
                            <Tooltip title={actionsData.reboot.title}>
                              <div>
                                <RefreshIcon onClick={() => handleRefresh(device)} />
                              </div>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="empty-action" />
                        )}
                        {![DeviceStatus.not_activated].includes(status) &&
                        hasOnlyStaffAdminPrivileges(currentUser?.permission_level) ? (
                          <div className="action">
                            <Tooltip title={actionsData.reset.title}>
                              <div>
                                <FactoryResetIcon onClick={() => handleReset(device)} />
                              </div>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="empty-action" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {showPagination && (
              <Pagination deviceCount={deviceCount} onActionClick={onActionClick} />
            )}
          </div>
        </div>
      </MainPage>
    </>
  );
};

DevicesComponent.propTypes = {
  deviceData: PropTypes.arrayOf(PropTypes.object),
  fetchingDeviceData: PropTypes.bool,
  onActionClick: PropTypes.func,
  providerId: PropTypes.string,
  deviceCount: PropTypes.number,
  showPagination: PropTypes.bool,
  offset: PropTypes.number,
  limit: PropTypes.number,
  userHubsData: PropTypes.arrayOf(PropTypes.object),
  setQueryParams: PropTypes.func,
};

export default DevicesComponent;
